import { useState } from 'react';
import { Mail, Check, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { z } from 'zod';
import { sanitizeText, sanitizeEmail } from '@/utils/sanitize';

const subscribeSchema = z.object({
  email: z.string().trim().email('Please enter a valid email').max(255),
  name: z.string().trim().max(100).optional(),
});

interface NewsletterSubscribeProps {
  variant?: 'inline' | 'card';
}

export function NewsletterSubscribe({ variant = 'card' }: NewsletterSubscribeProps) {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const { toast } = useToast();
  const { user } = useAuth();

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();
    setErrors({});
    
    const subscribeEmail = user?.email || email;
    
    if (!subscribeEmail) {
      setErrors({ email: 'Email is required' });
      return;
    }

    // Validate input
    const result = subscribeSchema.safeParse({
      email: subscribeEmail,
      name: name || undefined,
    });

    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.errors.forEach((err) => {
        if (err.path[0]) {
          fieldErrors[err.path[0] as string] = err.message;
        }
      });
      setErrors(fieldErrors);
      return;
    }

    setIsLoading(true);

    try {
      const { error } = await supabase
        .from('newsletter_subscribers')
        .upsert({
          email: sanitizeEmail(subscribeEmail),
          name: name ? sanitizeText(name) : null,
          user_id: user?.id || null,
          is_active: true,
          unsubscribed_at: null,
        }, {
          onConflict: 'email',
        });

      if (error) throw error;

      setIsSubscribed(true);
      toast({
        title: "Subscribed!",
        description: "You'll receive our weekly digest every Monday.",
      });
    } catch (error: any) {
      toast({
        title: "Subscription failed",
        description: error.message || "Please try again later.",
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (isSubscribed) {
    return (
      <div className={variant === 'card' ? 'p-6 rounded-xl bg-success/10 border border-success/20' : ''}>
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-success/20 flex items-center justify-center">
            <Check className="h-5 w-5 text-success" />
          </div>
          <div>
            <p className="font-semibold text-success">You're subscribed!</p>
            <p className="text-sm text-muted-foreground">Check your inbox every Monday.</p>
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'inline') {
    return (
      <form onSubmit={handleSubscribe} className="flex gap-2">
      {!user && (
        <div className="flex-1">
          <Input
            type="email"
            placeholder="your@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className={errors.email ? 'border-destructive' : ''}
            maxLength={255}
            required
          />
          {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
        </div>
      )}
        <Button type="submit" disabled={isLoading} size={user ? 'default' : 'default'}>
          {isLoading ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : (
            <>
              <Mail className="h-4 w-4 mr-2" />
              {user ? 'Subscribe to Digest' : 'Subscribe'}
            </>
          )}
        </Button>
      </form>
    );
  }

  return (
    <div className="p-6 rounded-xl bg-gradient-to-br from-primary/10 via-primary/5 to-transparent border">
      <div className="flex items-start gap-4">
        <div className="w-12 h-12 rounded-xl bg-primary/20 flex items-center justify-center shrink-0">
          <Mail className="h-6 w-6 text-primary" />
        </div>
        <div className="flex-1">
          <h3 className="font-semibold text-lg mb-1">Weekly Digest</h3>
          <p className="text-sm text-muted-foreground mb-4">
            Get the latest vibe coding tools and trending features delivered to your inbox every Monday.
          </p>
          <form onSubmit={handleSubscribe} className="space-y-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <Input
                type="text"
                placeholder="Your name (optional)"
                value={name}
                onChange={(e) => setName(e.target.value)}
                maxLength={100}
                className="flex-1"
              />
              {!user && (
                <div className="flex-1">
                  <Input
                    type="email"
                    placeholder="your@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={errors.email ? 'border-destructive flex-1' : 'flex-1'}
                    maxLength={255}
                    required
                  />
                  {errors.email && <p className="text-xs text-destructive mt-1">{errors.email}</p>}
                </div>
              )}
            </div>
            <Button type="submit" disabled={isLoading} className="shrink-0">
              {isLoading ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <Mail className="h-4 w-4 mr-2" />
              )}
              {user ? 'Subscribe with my email' : 'Subscribe'}
            </Button>
          </form>
          {user && (
            <p className="text-xs text-muted-foreground mt-2">
              We'll use your account email: {user.email}
            </p>
          )}
        </div>
      </div>
    </div>
  );
}
